'use client';

/**
 * The `multiSelect` field's editor — a closed set of options, more than one of
 * which can be chosen at once (VALE-129, `components/attributes/CLAUDE.md`
 * §3).
 *
 * **A popover over a checkable list, not a `Select`** — Radix's `Select` is
 * single-choice by construction, so the picker this treatment already has for
 * `select` cannot be reused for a set. `components/ui/user-multi-select-dropdown.tsx`
 * is the closest existing control (`Command` + `Popover`, a checkmark per row)
 * and is the interaction this is modelled on, but it is typed to `LawFirmUser[]`
 * and styled as a standalone dropdown — this is the same idiom rebuilt generic
 * over {@link AttributeOption}[] and dressed in the grid's own bare/unified
 * chrome, the same relationship `attribute-address-editor.tsx` has to a bespoke
 * address form.
 *
 * **The draft stays a `string`** — see the `multiSelect` arm's own doc in
 * `attribute-types.ts` for why. It is the selected values, comma-joined; empty
 * selection is `''`.
 */

import { CheckIcon } from 'lucide-react';
import {
  Command,
  CommandGroup,
  CommandItem,
  CommandList
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import {
  ATTRIBUTE_EDITOR_BARE,
  ATTRIBUTE_INSET,
  ATTRIBUTE_LEADING,
  ATTRIBUTE_POPOVER_COLLISION_PADDING,
  ATTRIBUTE_POPOVER_W,
  ATTRIBUTE_VALUE_TEXT
} from './attribute-tokens';
import { attributePlaceholder } from './attribute-placeholder';
import type { AttributeDraft, AttributeOption } from './attribute-types';
import {
  commandListToggleOnSpace,
  useAttributePopoverCommit
} from './use-attribute-popover-commit';

/** `''` decodes to no selection, never a one-element array holding `''`. */
function decode(value: string): string[] {
  return value ? value.split(',').filter(Boolean) : [];
}

/**
 * **The row's read-out — the chosen options' labels, in the order they were
 * chosen.** `null` when nothing is selected, so the caller supplies its own
 * empty state.
 *
 * Lifted out of the trigger's body and exported so the row's commit bridge
 * renders the identical string (VALE-1773) — see `attribute-draft-display.ts`.
 * An unknown value falls back to the raw value rather than disappearing, which
 * is what the list itself does.
 */
export function formatMultiSelectSummary(
  value: string,
  options: AttributeOption[]
): string | null {
  const selected = decode(value);
  if (selected.length === 0) return null;
  return selected
    .map(
      (optionValue) =>
        options.find((option) => option.value === optionValue)?.label ??
        optionValue
    )
    .join(', ');
}

export function AttributeMultiSelectEditor({
  label,
  value,
  options,
  onDraft,
  onRevert,
  onCommit
}: {
  label: string;
  /** The comma-joined draft — see this module's header. */
  value: string;
  options: AttributeOption[];
  onDraft: (next: AttributeDraft) => void;
  onRevert: () => void;
  onCommit: (value: AttributeDraft) => void;
}) {
  const selected = decode(value);

  const toggle = (optionValue: string) => {
    const next = selected.includes(optionValue)
      ? selected.filter((existing) => existing !== optionValue)
      : [...selected, optionValue];
    // Toggling is a draft update, not a commit — the popover stays open so
    // more than one option can be chosen in one visit, exactly the property a
    // `Select`'s "commits a chosen option immediately" cannot offer a set.
    onDraft(next.join(','));
  };

  /*
    `Enter` commits and closes, `Escape` reverts, dismissing commits: the one
    contract all seven composite popover editors share (VALE-1321). See
    `./use-attribute-popover-commit`, which owns every subtlety in it.
  */
  const popoverCommit = useAttributePopoverCommit({
    draft: value,
    onRevert,
    onCommit,
    // The panel is a `cmdk` list, which claims `Enter` the moment a click
    // gives it focus. See the flag's own doc.
    claimEnterBeforeList: true,
    // `p-0` below, because the `Command` brings its own padding — so the confirm
    // row has to carry the panel padding the panel is not carrying.
    confirmInset: true
  });

  return (
    <Popover {...popoverCommit.popover}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={label}
          className={cn(
            ATTRIBUTE_VALUE_TEXT,
            ATTRIBUTE_INSET,
            ATTRIBUTE_LEADING,
            'text-foreground min-w-0 flex-1 truncate rounded-md text-left',
            ATTRIBUTE_EDITOR_BARE
          )}
        >
          {formatMultiSelectSummary(value, options) ??
            attributePlaceholder(label)}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        collisionPadding={ATTRIBUTE_POPOVER_COLLISION_PADDING}
        /*
          The one composite-popover width, with the panel's padding dropped
          because a `Command` list brings its own, see `ATTRIBUTE_POPOVER_W`.
          It was a hand-typed `w-72`, one step narrower than every other
          composite popover on the treatment (VALE-1505).
        */
        className={cn(ATTRIBUTE_POPOVER_W, 'p-0')}
        {...popoverCommit.content}
      >
        <Command onKeyDown={commandListToggleOnSpace}>
          <CommandList>
            <CommandGroup>
              {options.map((option) => {
                const isSelected = selected.includes(option.value);
                return (
                  <CommandItem
                    key={option.value}
                    value={option.value}
                    onSelect={() => toggle(option.value)}
                    className={cn(ATTRIBUTE_VALUE_TEXT, 'cursor-pointer')}
                  >
                    <span className="min-w-0 flex-1 truncate">
                      {option.label}
                    </span>
                    <CheckIcon
                      className={cn(
                        'h-3.5 w-3.5 shrink-0',
                        isSelected ? 'opacity-100' : 'opacity-0'
                      )}
                    />
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
        {popoverCommit.confirm}
      </PopoverContent>
    </Popover>
  );
}
